import { useState, useRef } from 'react';
import { Loader2, Upload, X, FileVideo, ImageIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCreateTicket } from './useTickets';
import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_LABEL } from './types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** ID do cliente em contexto (opcional) */
  clientId?: string | null;
}

export function TicketModal({ open, onOpenChange, clientId }: Props) {
  const create = useCreateTicket();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setTitle('');
    setDescription('');
    setFile(null);
    setFileError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) {
      setFile(null);
      setFileError(null);
      return;
    }
    if (!ALLOWED_MIME_TYPES.includes(selected.type)) {
      setFileError(`Tipo não permitido. Use imagem (PNG/JPG/WebP) ou vídeo (MP4/MOV/WebM).`);
      setFile(null);
      return;
    }
    if (selected.size > MAX_FILE_SIZE_BYTES) {
      setFileError(
        `Arquivo muito grande (máx ${MAX_FILE_SIZE_LABEL}). Tamanho atual: ${(selected.size / 1024 / 1024).toFixed(1)}MB`,
      );
      setFile(null);
      return;
    }
    setFile(selected);
    setFileError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(selected.type.startsWith('image/') ? URL.createObjectURL(selected) : null);
  };

  const removeFile = () => {
    setFile(null);
    setFileError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canSubmit = title.trim().length >= 3 && description.trim().length >= 5 && !fileError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      await create.mutateAsync({
        title,
        description,
        ...(file && { file }),
        ...(clientId && { clientId }),
      });
      reset();
      onOpenChange(false);
    } catch {
      // toast já mostra
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-brand text-lg">Abrir chamado</DialogTitle>
          <DialogDescription>
            Encontrou um erro ou tem uma sugestão? Conte aqui — nosso time vai analisar.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="ticket-title">Título *</Label>
            <Input
              id="ticket-title"
              placeholder="Resumo curto do problema ou ideia"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ticket-desc">Descrição *</Label>
            <Textarea
              id="ticket-desc"
              placeholder="O que estava tentando fazer? O que aconteceu? Em qual página?"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label>Anexo (opcional)</Label>
            {!file ? (
              <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-border bg-white px-4 py-6 text-center transition-colors hover:border-primary hover:bg-primary/5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_MIME_TYPES.join(',')}
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Upload size={20} className="text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Clique para anexar</p>
                <p className="text-xs text-muted-foreground">
                  Imagem (PNG/JPG/WebP) ou vídeo (MP4/MOV/WebM) — máx {MAX_FILE_SIZE_LABEL}
                </p>
              </label>
            ) : (
              <div className="rounded-lg border border-border bg-white p-3">
                <div className="flex items-start gap-3">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="h-16 w-16 rounded object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded bg-muted">
                      {file.type.startsWith('video/') ? (
                        <FileVideo size={24} className="text-muted-foreground" />
                      ) : (
                        <ImageIcon size={24} className="text-muted-foreground" />
                      )}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={removeFile}
                    className="h-7 w-7"
                  >
                    <X size={14} />
                  </Button>
                </div>
              </div>
            )}
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
            <p className="text-xs text-muted-foreground">
              💡 Caso precise diminuir o tamanho do seu vídeo, use o app{' '}
              <a
                href="https://handbrake.fr/"
                target="_blank"
                rel="noreferrer"
                className="text-primary underline-offset-2 hover:underline"
              >
                HandBrake
              </a>{' '}
              ou algum site que reduza o vídeo.
            </p>
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose(false)}
              disabled={create.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit || create.isPending}
              className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
            >
              {create.isPending ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Enviando…
                </>
              ) : (
                'Abrir chamado'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
